import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  canEditScheduledMessage,
  getScheduledMessageActionHandler,
  getScheduledMessagePrimaryDispatchAction,
  getScheduledMessageSecondaryActions,
  isScheduledMessageActionLoading,
} from '../scheduledMessageActions';

const primaryButtonClass =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-forge-600 px-4 py-2 text-sm font-medium text-white hover:bg-forge-700 transition-colors disabled:opacity-40 disabled:pointer-events-none';

const secondaryButtonClass =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-content-primary hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:pointer-events-none';

const dangerMenuItemClass =
  'block w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:pointer-events-none';

const menuItemClass =
  'block w-full text-left px-3 py-2 text-sm text-content-primary hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:pointer-events-none';

function ScheduledMessageDetailActions({
  item,
  editTo,
  loadingState,
  onCancel,
  onDelete,
  onRetry,
  onResend,
  onForceDispatch,
  onSendTemplate,
  onBack,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const handlers = {
    onCancel,
    onDelete,
    onRetry,
    onResend,
    onForceDispatch,
    onSendTemplate,
  };

  const canEdit = canEditScheduledMessage(item);
  const primaryAction = getScheduledMessagePrimaryDispatchAction(item);
  const secondaryActions = getScheduledMessageSecondaryActions(item);
  const isBusy = Boolean(loadingState?.activeActionId);
  const hasMoreActions = secondaryActions.length > 0;

  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight ?? 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8;

    setMenuStyle({
      position: 'fixed',
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
      visibility: 'visible',
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();
    const frame = requestAnimationFrame(updateMenuPosition);

    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [menuOpen, updateMenuPosition, secondaryActions.length]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      const inButton = buttonRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inButton && !inMenu) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const runAction = (action) => {
    setMenuOpen(false);
    const handler = getScheduledMessageActionHandler(action.id, handlers);
    handler?.();
  };

  const renderActionButton = (action, className) => {
    const loading = isScheduledMessageActionLoading(action.id, loadingState);
    const handler = getScheduledMessageActionHandler(action.id, handlers);

    return (
      <button
        key={action.id}
        type="button"
        onClick={handler}
        disabled={isBusy}
        className={className}
        title={action.description}
      >
        {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />}
        {loading ? action.loadingLabel : action.label}
      </button>
    );
  };

  const menuPanel = menuOpen ? (
    <div
      ref={menuRef}
      style={
        menuStyle ?? {
          position: 'fixed',
          top: -9999,
          left: 0,
          visibility: 'hidden',
        }
      }
      className="min-w-[14rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
      role="menu"
    >
      {secondaryActions.map((action) => {
        const loading = isScheduledMessageActionLoading(action.id, loadingState);
        const itemClass = action.variant === 'danger' ? dangerMenuItemClass : menuItemClass;

        return (
          <button
            key={action.id}
            type="button"
            onClick={() => runAction(action)}
            disabled={isBusy}
            className={itemClass}
            role="menuitem"
            title={action.description}
          >
            {loading ? action.loadingLabel : action.label}
          </button>
        );
      })}
    </div>
  ) : null;

  if (!canEdit && !primaryAction && !hasMoreActions) {
    return (
      <section className="surface-card p-4">
        <button type="button" onClick={onBack} className={secondaryButtonClass}>
          Voltar à listagem
        </button>
      </section>
    );
  }

  return (
    <section className="surface-card p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-content-primary">Ações</h2>
          <p className="mt-1 text-xs text-content-muted">
            {primaryAction?.description
              || (hasMoreActions
                ? 'Gerencie envios e manutenção deste agendamento'
                : 'Altere ou volte para a listagem')}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {canEdit && (
            <Link to={editTo} className={`${secondaryButtonClass} text-center`}>
              Editar
            </Link>
          )}

          {primaryAction && renderActionButton(primaryAction, primaryButtonClass)}

          {hasMoreActions && (
            <button
              ref={buttonRef}
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              disabled={isBusy}
              className={secondaryButtonClass}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              Mais ações
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
          )}

          <button type="button" onClick={onBack} className={`${secondaryButtonClass} sm:ml-1`}>
            Voltar
          </button>
        </div>
      </div>

      {menuOpen && createPortal(menuPanel, document.body)}
    </section>
  );
}

export default ScheduledMessageDetailActions;
